import { ThemeEvents } from '@theme/events';

const SIZE_RE = /W:\s*([\d.]+)"?\s*D:\s*([\d.]+)"?\s*H:\s*([\d.]+)"?/;
const AXES = [
  { key: 'w', label: 'Width' },
  { key: 'd', label: 'Depth' },
  { key: 'h', label: 'Height' },
];

class ConfiguratorSizeMatrix extends HTMLElement {
  connectedCallback() {
    // Listen on the closest section rather than document, matching the pattern
    // in configuration-summary.js. Resolved before init() so the price lookup
    // below can scope to it too.
    this.section = this.closest('.shopify-section, dialog');

    // The price payload (<script data-configurator-prices>) is emitted OUTSIDE
    // <variant-picker> by the block (see configurator-options.liquid) so it can
    // never be mistaken for the variant JSON that variant-picker.js looks up via
    // `variant-picker script[type="application/json"]`. That means it is not a
    // descendant of this element, so it can't be read with this.querySelector;
    // scope to the shared section wrapper instead. connectedCallback only fires
    // once (see comment below), which is fine: price data is a property of the
    // product, not the current selection, so it never goes stale mid-session.
    const priceEl = (this.section || document).querySelector('[data-configurator-prices]');
    try {
      this.prices = priceEl ? JSON.parse(priceEl.textContent) : {};
    } catch {
      this.prices = {};
    }
    this.currency = document.documentElement.dataset.currency || 'USD';
    // I3(a): the merchant-editable "no upcharge" wording, passed through from
    // blocks/configurator-options.liquid's `included_label` setting so the
    // generated Depth/Height cards say the same thing the server-rendered
    // Finish/Hardware cards say instead of leaving an empty price slot. Falls
    // back to the same literal that setting defaults to.
    this.includedLabel = this.dataset.includedLabel || 'Included';
    // 2026-10-12 (Barry): depth/height cards with no upcharge no longer say "Included";
    // the standard depth gets its own note instead (block settings standard_depth and
    // standard_depth_label, defaults 21 and "Standard Depth").
    this.standardDepth = this.dataset.standardDepth || '';
    this.standardDepthLabel = this.dataset.standardDepthLabel || '';

    this.init();

    // Horizon's variant-picker morphs the section's DOM in place after a successful
    // fetch, so connectedCallback never fires again. Rebuild the axes on the
    // bubbling `variant:update` event instead.
    if (this.section && !this.listenerAttached) {
      this.section.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
      this.listenerAttached = true;
    }
  }

  disconnectedCallback() {
    if (this.section && this.listenerAttached) {
      this.section.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
      this.listenerAttached = false;
    }
  }

  handleVariantUpdate = () => {
    this.init();
  };

  /**
   * Builds (or rebuilds) the axis UI from whatever real size inputs currently
   * exist in the DOM. Must be safe to call repeatedly: a morph replaces the
   * fetched section's contents with fresh server HTML (fallback list only,
   * no axis markup, no `hidden` attribute), so every call has to start clean.
   */
  init() {
    // Drop any axis UI from a previous init() call before rebuilding. Query
    // fresh rather than trusting a stored reference: a morph may already have
    // removed it, or may not have touched it at all.
    this.querySelector(':scope > .configurator-size-matrix__axes')?.remove();
    this.ui = undefined;

    // Re-query: old input/entry references are stale after a morph.
    this.realInputs = Array.from(this.querySelectorAll('input[data-size-real]'));
    this.fallback = this.querySelector('[data-size-fallback]');
    if (!this.realInputs.length) return;

    // I4: availability is carried through the parse alongside the three axes so
    // an axis value can be marked unavailable when NO combination containing it
    // can be bought. Read from BOTH `input.disabled` and `data-option-available`:
    // `disabled` is the property that actually decides whether `pick()`'s
    // `.click()` can work at all, so tying the JS notion of "available" to it
    // keeps the two from ever disagreeing; `data-option-available` catches a
    // hypothetical future markup change that marks a value unavailable without
    // disabling it. Both default toward AVAILABLE when absent, so a missing
    // attribute degrades to today's behaviour rather than disabling the UI.
    this.entries = this.realInputs.map((input) => {
      const m = SIZE_RE.exec(input.dataset.optionValueName || '');
      return m
        ? {
            input,
            w: m[1],
            d: m[2],
            h: m[3],
            available: !input.disabled && input.dataset.optionAvailable !== 'false',
          }
        : null;
    });

    // Any unparseable value: leave the stock combined list visible and do nothing.
    if (this.entries.some((e) => e === null)) {
      if (this.fallback) this.fallback.hidden = false;
      return;
    }

    this.selected = {};
    const current = this.entries.find((e) => e.input.checked) || this.entries[0];
    AXES.forEach(({ key }) => { this.selected[key] = current[key]; });

    // render() skips any axis with fewer than 2 unique values, so a product whose
    // sizes collapse to a single choice on every axis renders no axes at all.
    // Only hide the no-JS fallback when an axis actually rendered; otherwise the
    // customer would be left with no size UI whatsoever.
    const rendered = this.render();
    if (this.fallback) this.fallback.hidden = rendered;
  }

  valuesFor(key) {
    return [...new Set(this.entries.map((e) => e[key]))].sort((a, b) => parseFloat(a) - parseFloat(b));
  }

  /**
   * I4: an axis value is unavailable only when EVERY parsed combination
   * containing it is unavailable, i.e. there is genuinely nothing the customer
   * could buy at that width/depth/height. A value that is merely unavailable in
   * combination with the CURRENT other axes stays enabled, because picking it is
   * still meaningful -- `pick()` relaxes the other axes onto a combination that
   * exists and can be bought.
   * @param {string} key
   * @param {string} value
   * @returns {boolean}
   */
  isAxisValueAvailable(key, value) {
    return this.entries.some((e) => e[key] === value && e.available);
  }

  /**
   * Builds the axis fieldsets. Returns true if at least one axis was rendered
   * and appended, false if every axis had fewer than 2 unique values (nothing
   * to choose). In the false case nothing is appended, so no empty container is
   * left in the DOM and `this.ui` stays unset for the next init().
   * @returns {boolean}
   */
  render() {
    this.ui = document.createElement('div');
    this.ui.className = 'configurator-size-matrix__axes';

    AXES.forEach(({ key, label }) => {
      const values = this.valuesFor(key);
      if (values.length < 2) return; // single-value axis is not a choice

      const fs = document.createElement('fieldset');
      fs.className = 'configurator-group';
      fs.dataset.configuratorGroup = label;

      // M2: server-rendered groups (blocks/configurator-options.liquid) put the
      // current selection next to the group name in the legend; these generated
      // ones only had the name, so Width/Depth/Height read differently from
      // Finish/Hardware for no reason. The value is filled in (and kept current)
      // by updateLegendValues() rather than being written here, so there is one
      // place that owns it for both the initial render and every later pick().
      const legend = document.createElement('legend');
      legend.className = 'configurator-group__legend';
      legend.innerHTML =
        `<span class="configurator-group__name">${label}</span>` +
        `<span class="configurator-group__value" data-axis-value></span>`;
      fs.appendChild(legend);

      const grid = document.createElement('div');
      grid.className = 'configurator-group__cards';
      grid.dataset.columns = '3';

      values.forEach((v) => {
        // I4: same disabled + struck treatment spec section 9 requires and
        // Ruling 22 gave Finish/Hardware. Disabling THIS input is what makes the
        // whole thing safe for the size composer: it is a pure proxy that only
        // ever receives real user clicks, so disabling it removes the value from
        // selection entirely, while the REAL size input that pick() drives via
        // .click() is a different element. syncChecked() sets `.checked` on
        // these directly, which still works on a disabled radio.
        const available = this.isAxisValueAvailable(key, v);
        const card = document.createElement('label');
        card.className = available ? 'config-card' : 'config-card config-card--unavailable';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `cfg-axis-${key}`;
        input.value = v;
        input.checked = v === this.selected[key];
        if (!available) {
          input.disabled = true;
          input.setAttribute('aria-disabled', 'true');
        }
        // Stop this synthetic radio's own change event from also bubbling to
        // variant-picker: pick() below drives the real input via .click(), which
        // fires its own independent (and correctly-attributed) change event. Without
        // this, variant-picker would handle two change events per click: one for
        // this proxy input (no data-option-value-id, so it dilutes the request) and
        // one for the real input, doubling the fetch and racing an abort.
        input.addEventListener('change', (event) => {
          event.stopPropagation();
          this.pick(key, v);
        });
        const text = document.createElement('span');
        text.className = 'config-card__text';
        text.innerHTML =
          `<span class="config-card__label">${v}"</span>` +
          `<span class="config-card__price" data-axis-price></span>`;
        card.append(input, text);
        grid.appendChild(card);
      });

      fs.appendChild(grid);
      this.ui.appendChild(fs);
    });

    if (!this.ui.children.length) {
      this.ui = undefined;
      return false;
    }

    this.appendChild(this.ui);
    this.updateLegendValues();
    this.updatePrices();
    return true;
  }

  /**
   * M2: mirrors the `.configurator-group__value` span the server-rendered
   * groups carry, showing the axis's current selection next to its name.
   * Called after every render() and after every pick(), because a pick()
   * can move axes the customer did not click (see pick()'s relaxation) and
   * the section morph that would otherwise refresh this only lands later.
   */
  updateLegendValues() {
    if (!this.ui) return;
    AXES.forEach(({ key, label }) => {
      const slot = this.ui.querySelector(`[data-configurator-group="${label}"] [data-axis-value]`);
      if (slot) slot.textContent = this.selected[key] === undefined ? '' : `${this.selected[key]}"`;
    });
  }

  pick(key, value) {
    this.selected[key] = value;

    const isExact = (e) => AXES.every(({ key: k }) => e[k] === this.selected[k]);
    const isOnAxis = (e) => e[key] === value;

    // Resolution order, most to least desirable. The first two clauses are the
    // I4 addition: prefer a combination that can actually be bought, keeping the
    // other axes on a combination that EXISTS (the pre-I4 behaviour) but
    // upgrading it to one that exists AND is available. The last two clauses are
    // the pre-I4 behaviour verbatim, so with zero unavailable variants -- the
    // catalogue's state today -- clause 1 always wins and nothing changes.
    const match =
      this.entries.find((e) => isExact(e) && e.available) ||
      this.entries.find((e) => isOnAxis(e) && e.available) ||
      this.entries.find(isExact) ||
      this.entries.find(isOnAxis);

    if (!match) return;

    // Adopt whichever combination was resolved. In the common (clause 1) case
    // this is a no-op re-assignment of what `this.selected` already held.
    AXES.forEach(({ key: k }) => {
      this.selected[k] = match[k];
    });
    this.syncChecked();
    this.updateLegendValues();
    // Update width prices immediately against the newly picked depth/height,
    // ahead of the network round trip triggered by the real input click below.
    this.updatePrices();

    // I4 SAFETY GUARD -- the second-order trap. `.click()` on a disabled input
    // is a silent no-op, which would kill the whole size selector rather than
    // just that one combination. This can only be reached if every combination
    // containing `value` is unavailable, and in that case the axis input the
    // customer would have had to click is itself rendered disabled (see
    // isAxisValueAvailable + render), so it is a backstop, not a live path.
    // Warned rather than thrown, matching configurator-edit-jump.js's precedent.
    if (match.input.disabled) {
      console.warn(`configurator-size-matrix: no available size for ${key} = "${value}"`);
      return;
    }
    // Native click fires a bubbling `change`, which variant-picker.js listens for.
    match.input.click();
  }

  /**
   * M3: `minimumFractionDigits: 0` with no maximum inherits the currency's own
   * maximum (2 for USD) only for the upper bound, but leaves the lower bound at
   * 0 -- so a non-round price rendered as `$690.5`, a cents value silently
   * truncated to one digit. Pinning the maximum to 2 keeps whole prices short
   * ($625, which is every price on the catalogue today) while rendering an odd
   * one in full ($690.50). Deliberately NOT `maximumFractionDigits: 0`: that
   * would round a real price to a different number, which is worse than an
   * inconsistent-looking one.
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  /**
   * Resolves the price, in cents, of the combination formed by taking the
   * current selection and substituting `value` on `key`. If that exact
   * combination doesn't exist, falls back to the lowest price among the
   * combinations that do have that value on that axis. Returns undefined when
   * nothing resolves (unknown size key in the price payload, empty payload).
   * @param {string} key
   * @param {string} value
   * @returns {number | undefined}
   */
  priceFor(key, value) {
    const wanted = { ...this.selected, [key]: value };
    let entry = this.entries.find((e) => AXES.every(({ key: k }) => e[k] === wanted[k]));
    if (!entry) {
      entry = this.entries
        .filter((e) => e[key] === value)
        .sort(
          (a, b) =>
            (this.prices[a.input.dataset.optionValueName] ?? Infinity) -
            (this.prices[b.input.dataset.optionValueName] ?? Infinity)
        )[0];
    }
    return entry ? this.prices[entry.input.dataset.optionValueName] : undefined;
  }

  /**
   * Fills every axis card's price slot.
   *
   * Width keeps spec section 7.4's rule: the ABSOLUTE price of the variant at
   * that width combined with the currently selected depth and height.
   *
   * I3(a): Depth and Height previously fell through this method entirely (it
   * early-returned unless the Width group existed) and were left with an empty
   * price slot, while spec 7.4 says a same-price option reads "Included". They
   * now render a real comparison against the current selection: `includedLabel`
   * when the price is unchanged, a signed delta when it is not. Today every
   * depth and height is the same price as its sibling, so every one of these
   * renders `Included` -- but it is now DERIVED rather than assumed, so a future
   * priced depth shows its upcharge instead of silently reading "Included".
   */
  updatePrices() {
    if (!this.ui) return;
    const currentCents = this.priceFor('w', this.selected.w);

    AXES.forEach(({ key, label }) => {
      const group = this.ui.querySelector(`[data-configurator-group="${label}"]`);
      if (!group) return;
      group.querySelectorAll('.config-card').forEach((card) => {
        const slot = card.querySelector('[data-axis-price]');
        if (!slot) return;
        const value = card.querySelector('input').value;
        const cents = this.priceFor(key, value);

        if (key === 'w') {
          slot.textContent = cents === undefined ? '' : this.formatMoney(cents);
          return;
        }

        if (cents === undefined || currentCents === undefined || cents === currentCents) {
          slot.textContent = key === 'd' && this.standardDepth && value === this.standardDepth ? this.standardDepthLabel : '';
          return;
        }
        const delta = cents - currentCents;
        slot.textContent = `${delta > 0 ? '+' : '-'}${this.formatMoney(Math.abs(delta))}`;
      });
    });
  }

  syncChecked() {
    // pick() now calls this on EVERY pick (not only when it had to relax the
    // other axes), so the previously-unreachable unguarded `this.ui` deref is
    // no longer worth leaving to an argument about reachability.
    if (!this.ui) return;
    AXES.forEach(({ key }) => {
      const input = this.ui.querySelector(`input[name="cfg-axis-${key}"][value="${this.selected[key]}"]`);
      if (input) input.checked = true;
    });
  }
}

if (!customElements.get('configurator-size-matrix')) {
  customElements.define('configurator-size-matrix', ConfiguratorSizeMatrix);
}
